/**
 * User Profile Zustand Store
 *
 * Centralized state management for user profile data.
 * Replaces React hooks with Zustand for consistent state
 * and prevents re-fetch loops on MFA errors.
 *
 * @module stores/userProfileStore
 */

import { create } from 'zustand';
import { apiService } from '@/services/ApiService';
import { UserApiError } from '@/services/domains/UserDomain';
import type { UserProfile, UserRole } from '@/types/user';
import { useMfaStore } from './mfaStore';
import { getLogger } from '@/services/shared/LoggerFactory';

type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

interface UserProfileState {
  /** Current user profile data */
  profile: UserProfile | null;
  /** Loading/fetching status */
  status: AsyncStatus;
  /** Error state for profile operations */
  error: UserApiError | null;
  /** Whether profile data has been loaded at least once */
  isInitialized: boolean;
  /** Track if a fetch is in progress to prevent duplicates */
  fetchInProgress: boolean;
}

interface UserProfileActions {
  /** Fetch current user profile */
  fetchProfile: () => Promise<void>;
  /** Update user profile */
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  /** Clear error state */
  clearError: () => void;
  /** Reset all state to initial values */
  reset: () => void;
  /** Check if user has specific role */
  hasRole: (role: UserRole) => boolean;
  /** Set profile directly (for external updates) */
  setProfile: (profile: UserProfile | null) => void;
}

type UserProfileStore = UserProfileState & UserProfileActions;

const initialState: UserProfileState = {
  profile: null,
  status: 'idle',
  error: null,
  isInitialized: false,
  fetchInProgress: false,
};

const logger = getLogger();

/**
 * Check if the error is an MFA required error
 */
function isMfaError(error: unknown): boolean {
  if (error instanceof UserApiError) {
    return error.status === 403 && error.code === 'MFA_REQUIRED';
  }
  return false;
}

export const useUserProfileStore = create<UserProfileStore>((set, get) => ({
  ...initialState,

  fetchProfile: async () => {
    const state = get();

    // Don't fetch if already in progress
    if (state.fetchInProgress) {
      logger.debug('User profile fetch already in progress, skipping');
      return;
    }

    // Check if MFA is required - don't make API calls
    if (useMfaStore.getState().mfaRequired) {
      logger.debug('MFA required, skipping user profile fetch');
      return;
    }

    set({ status: 'loading', error: null, fetchInProgress: true });

    try {
      const profile = await apiService.user.getCurrentUser();
      set({
        profile,
        status: 'success',
        error: null,
        isInitialized: true,
        fetchInProgress: false,
      });
    } catch (error) {
      const apiError = error as UserApiError;

      // If MFA required, don't set error - the interceptor handles redirect
      if (isMfaError(error)) {
        logger.debug('MFA required error, halting profile fetch');
        set({
          status: 'idle',
          fetchInProgress: false,
          // Don't set error - MFA flow will handle it
        });
        return;
      }

      logger.error('User profile fetch failed:', apiError);
      set({
        error: apiError,
        status: 'error',
        isInitialized: true, // Mark as initialized even on error to prevent loops
        fetchInProgress: false,
      });
    }
  },

  updateProfile: async (profileData: Partial<UserProfile>) => {
    // Check if MFA is required - don't make API calls
    if (useMfaStore.getState().mfaRequired) {
      logger.debug('MFA required, skipping profile update');
      return;
    }

    set({ status: 'loading', error: null });

    try {
      const updatedProfile = await apiService.user.updateProfile(profileData);
      set({
        profile: updatedProfile,
        status: 'success',
        error: null,
      });
    } catch (error) {
      const apiError = error as UserApiError;

      // If MFA required, don't set error
      if (isMfaError(error)) {
        set({ status: 'idle' });
        return;
      }

      logger.error('User profile update failed:', apiError);
      set({
        error: apiError,
        status: 'error',
      });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },

  hasRole: (role: UserRole) => {
    return get().profile?.role === role;
  },

  setProfile: (profile: UserProfile | null) => {
    set({
      profile,
      isInitialized: profile !== null,
      status: profile !== null ? 'success' : 'idle',
    });
  },
}));

/**
 * Selector for loading state
 */
export const selectIsLoading = (state: UserProfileStore): boolean =>
  state.status === 'loading';

/**
 * Selector for error state
 */
export const selectError = (state: UserProfileStore): UserApiError | null =>
  state.error;

/**
 * Selector for profile
 */
export const selectProfile = (state: UserProfileStore): UserProfile | null =>
  state.profile;

/**
 * Selector for initialized state
 */
export const selectIsInitialized = (state: UserProfileStore): boolean =>
  state.isInitialized;

